import { useCallback, useRef, useState } from 'react';
import { useRecoilCallback, useRecoilState } from 'recoil';
import {
    downloadInstancesToFiles,
    isAbortError,
} from '~/backend/connector-next-api/dataRetrieval';
import { fetchPatientsGetById } from '~/backend/connector-next-api/connector-next-components';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { registerLoadedDataset } from '~/domain/persistence/registerLoadedDataset';
import { readSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import { connectorViewerOpenPhaseState } from '~/state/atoms/dicomProcessorAtoms';
import { buildConnectorManifest } from './buildConnectorManifest';
import { resolveOpenTargets } from './resolveOpenTargets';
import { manifestsBySeriesUid, planProgressiveOpen } from './streamingPlan';
import {
    useIncompleteRetrievalDecision,
    type IncompleteRetrievalDecision,
} from './useIncompleteRetrievalDecision';
import { useOpenNotify } from './useOpenNotify';
import { useSliceStreaming } from './useSliceStreaming';

export type LoadDataset = {
    /** Retrieves one of the patient's datasets into the open session. */
    loadDataset: (datasetId: string) => void;
    /** Dataset id currently being retrieved, or undefined when idle. */
    loadingDatasetId: string | undefined;
    /** The question put to the user when the retrieval came up short. */
    incompleteRetrieval: Pick<IncompleteRetrievalDecision, 'state' | 'settle'>;
};

/**
 * Brings one more of the patient's datasets into a session that is already
 * open — the PET nobody asked for at the start, the second structure set.
 *
 * This is the open pipeline minus the parts that make it an *open*: no
 * handoff, no navigation, no environment initialisation. The commit merges
 * into the loaded patient (`toRepository` adds series to the row it finds by
 * DICOM Patient ID), which is the same path the Setup step's local file drop
 * has always taken.
 *
 * It holds the same exclusive-feed lock a worklist open does. The DICOM
 * processor is shared and single-threaded; two feeds into it interleave into
 * one patient's data and neither notices. It also keeps the open's other
 * guards: a series still being received is refused, and a short retrieval is
 * put to the user before anything is committed.
 */
export function useLoadDataset(patientId: string | undefined): LoadDataset {
    const [phase, setPhase] = useRecoilState(connectorViewerOpenPhaseState);
    const session = useSessionOrNull();
    const { processImportedFiles } = useProcessDicomFiles();
    const { startStreaming } = useSliceStreaming();
    const notify = useOpenNotify();
    const incompleteRetrieval = useIncompleteRetrievalDecision();
    const { awaitDecision } = incompleteRetrieval;

    // Read when the call runs, not when `load` was created: the user can
    // switch the primary image while a load waits on the network or on
    // the shortfall question.
    const readPrimarySeriesUid = useRecoilCallback(
        ({ snapshot }) =>
            (): string | undefined =>
                readSelectedPrimaryImage(snapshot)?.seriesUid,
        []
    );

    const [loadingDatasetId, setLoadingDatasetId] = useState<string>();
    // Written synchronously, so a second click in the same tick is refused
    // where a Recoil read would still see `idle`.
    const runningRef = useRef(false);

    const load = useCallback(
        async (datasetId: string, signal: AbortSignal): Promise<void> => {
            if (patientId === undefined) {
                return;
            }
            setPhase('resolving');

            // The detail is re-fetched rather than remembered: it carries the
            // slice list this dataset must be addressed by, and it is the same
            // shape the worklist open resolves against, so one set of rules
            // covers both.
            const detail = await fetchPatientsGetById(
                { pathParams: { id: patientId } },
                signal
            );
            // Resolved as a selection of exactly one, not filtered out of the
            // whole patient: the shortfall counts that come back then describe
            // this dataset rather than every dataset the patient has. The
            // selection key is the dataset id, which is what `seriesKey`
            // prefers.
            const { series, unresolved, receiving, unaddressableSlices } =
                resolveOpenTargets(detail, [datasetId]);
            if (receiving > 0) {
                notify('patientList.open.seriesStillArriving', 'warning', {
                    count: receiving,
                });
                return;
            }
            const wanted = series[0];
            if (wanted === undefined || unresolved > 0) {
                notify('patientList.open.someUnresolved', 'warning', {
                    count: 1,
                });
                return;
            }

            setPhase('downloading');
            const plan = await planProgressiveOpen([wanted], { signal }).catch(
                () => ({
                    blocking: wanted.instances,
                    streaming: [],
                })
            );

            const { files, skipped } = await downloadInstancesToFiles(
                plan.blocking,
                { signal }
            );
            if (signal.aborted) {
                return;
            }
            if (files.length === 0) {
                notify('patientList.open.retrieveFailed', 'error');
                return;
            }

            // The gap would be interpolated into synthesized anatomy on
            // commit, so the user decides before it, not after.
            const missing = skipped.length + unaddressableSlices;
            if (missing > 0) {
                setPhase('confirming');
                const proceed = await awaitDecision(files.length, missing);
                if (signal.aborted || !proceed) {
                    return;
                }
            }

            setPhase('processing');
            // This flow set the lock two lines up, so it has to declare
            // itself its holder: the processor's guard refuses any feed
            // arriving while the phase is not idle, this one included.
            const result = await processImportedFiles(files, {
                holdsOpenLock: true,
                // The same geometry the initial open passes. A progressive
                // plan brings ONE anchor slice, so without the manifest the
                // processor reconstructs a one-slice volume and the streaming
                // below then writes the remaining slices into the wrong grid.
                imageManifests: manifestsBySeriesUid(plan.streaming),
            });
            if (result === undefined) {
                return;
            }

            // The dataset came from the connector, so it must carry the
            // connector's identity for it. Without a ref the save workflow
            // reads it as a locally created object and a save writes a
            // second copy of the row this load just read.
            //
            // The identity, version, approval and locks are read from the
            // detail fetched for this load, not from the manifest the open
            // captured: a lock or approval set since then would otherwise be
            // missing here, and the version token would make the first save
            // a conflict.
            if (session) {
                const claimed = registerLoadedDataset(
                    session.persistenceRegistry,
                    buildConnectorManifest(detail),
                    datasetId,
                    result,
                    session.context.tracker
                );
                if (claimed) {
                    session.pendingChangesStore.adoptPersisted([claimed]);
                }
            }

            // Only after a successful commit: streaming into a volume the
            // repository does not hold would fetch pixels nobody can see.
            // The primary image streams first; this load does not change it.
            if (plan.streaming.length > 0) {
                startStreaming(
                    result.images,
                    plan.streaming,
                    readPrimarySeriesUid()
                );
            }
            // Confirming a short load is not the end of telling the user: the
            // dataset now holds interpolated slices, so the shortfall stays on
            // screen until dismissed (SR-WOR-H-37-1). Only on a commit that
            // stood; one refused or rolled back loaded nothing to warn about.
            if (missing > 0) {
                notify(
                    'patientList.open.someSkipped',
                    'warning',
                    { count: missing },
                    true
                );
            }
        },
        [
            patientId,
            setPhase,
            notify,
            processImportedFiles,
            startStreaming,
            readPrimarySeriesUid,
            session,
            awaitDecision,
        ]
    );

    const loadDataset = useCallback(
        (datasetId: string) => {
            if (runningRef.current || phase !== 'idle') {
                notify('patientList.open.inFlight', 'warning');
                return;
            }
            runningRef.current = true;
            setLoadingDatasetId(datasetId);
            const controller = new AbortController();

            void load(datasetId, controller.signal)
                .catch((error: unknown) => {
                    if (!isAbortError(error)) {
                        notify('patientList.open.retrieveFailed', 'error');
                    }
                })
                .finally(() => {
                    runningRef.current = false;
                    setLoadingDatasetId(undefined);
                    setPhase('idle');
                });
        },
        [phase, load, notify, setPhase]
    );

    return {
        loadDataset,
        loadingDatasetId,
        incompleteRetrieval: {
            state: incompleteRetrieval.state,
            settle: incompleteRetrieval.settle,
        },
    };
}
